import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Car, Search, AlertCircle, Bookmark } from 'lucide-react';
import { api } from '../lib/api';
import { formatBRL, formatDateBR, VEHICLE_STATUS } from '../lib/format';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';
import VehicleStatusBadge from '../components/VehicleStatusBadge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';

export default function Vehicles() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const load = async () => {
    setLoading(true);
    const params = {};
    if (statusFilter !== 'ALL') params.status = statusFilter;
    if (search) params.search = search;
    try {
      const { data } = await api.get('/vehicles', { params });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    load();
  };

  return (
    <>
      <PageHeader
        title="Veículos"
        subtitle="Cadastre, acompanhe o status e analise o resultado financeiro de cada veículo da operação."
        actions={
          <Link to="/veiculos/novo">
            <Button className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="vehicles-new-button">
              <Plus className="h-4 w-4 mr-1.5" /> Novo veículo
            </Button>
          </Link>
        }
      >
        <div className="flex flex-col md:flex-row gap-3">
          <form onSubmit={onSearchSubmit} className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por marca, modelo ou placa..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="vehicles-search-input"
            />
          </form>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full md:w-56" data-testid="vehicles-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos os status</SelectItem>
              {Object.entries(VEHICLE_STATUS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </PageHeader>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Car}
          title="Nenhum veículo encontrado"
          description="Cadastre seu primeiro veículo informando dados, valor de compra e a composição societária."
          action={{ label: 'Cadastrar veículo', onClick: () => navigate('/veiculos/novo') }}
        />
      ) : (
        <Card className="border-border/70 overflow-hidden">
          <CardContent className="p-0">
            {/* Desktop table */}
            <div className="overflow-x-auto hidden md:block">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-muted-foreground">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Veículo</th>
                    <th className="text-left px-4 py-3 font-medium">Placa</th>
                    <th className="text-right px-4 py-3 font-medium">Compra</th>
                    <th className="text-right px-4 py-3 font-medium">Venda</th>
                    <th className="text-left px-4 py-3 font-medium">Data Compra</th>
                    <th className="text-left px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((v) => (
                    <tr
                      key={v.id}
                      onClick={() => navigate(`/veiculos/${v.id}`)}
                      className="border-t border-border/70 hover:bg-muted/30 cursor-pointer transition-colors"
                      data-testid="vehicle-row"
                    >
                      <td className="px-4 py-3 font-medium">
                        {v.brand} {v.model} <span className="text-muted-foreground font-normal">{v.year}</span>
                        {v.is_stub && (
                          <span className="ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-200" title="Cadastro pendente">
                            <AlertCircle className="h-2.5 w-2.5 mr-0.5" /> Pendente
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{v.plate || '—'}</td>
                      <td className="px-4 py-3 text-right tabular">{formatBRL(v.purchase_price)}</td>
                      <td className="px-4 py-3 text-right tabular">{v.sale_price ? formatBRL(v.sale_price) : '—'}</td>
                      <td className="px-4 py-3 text-muted-foreground">{formatDateBR(v.purchase_date)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <VehicleStatusBadge status={v.status} />
                          {v.is_reserved && (
                            <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-900 border border-amber-200">
                              <Bookmark className="h-2.5 w-2.5" /> Reservado
                            </span>
                          )}
                          {v.is_direct_passthrough && (
                            <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-900 border border-purple-200" title="Passagem direta">
                              ↪ Passagem
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-border/70">
              {items.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => navigate(`/veiculos/${v.id}`)}
                  className="w-full text-left p-4 hover:bg-muted/40 transition-colors"
                  data-testid="vehicle-row"
                >
                  <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
                    <div className="font-semibold">
                      {v.brand} {v.model} <span className="text-muted-foreground font-normal text-sm">{v.year}</span>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <VehicleStatusBadge status={v.status} />
                      {v.is_reserved && (
                        <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-900 border border-amber-200">
                          <Bookmark className="h-2.5 w-2.5" /> Reservado
                        </span>
                      )}
                      {v.is_direct_passthrough && (
                        <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium bg-purple-100 text-purple-900 border border-purple-200" title="Passagem direta">
                          ↪ Passagem
                        </span>
                      )}
                      {v.is_stub && (
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-200">
                          <AlertCircle className="h-2.5 w-2.5 mr-0.5" /> Pendente
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <div className="text-muted-foreground">Placa</div>
                      <div className="tabular">{v.plate || '—'}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Data compra</div>
                      <div className="tabular">{formatDateBR(v.purchase_date)}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Compra</div>
                      <div className="tabular font-medium">{formatBRL(v.purchase_price)}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Venda</div>
                      <div className="tabular font-medium">{v.sale_price ? formatBRL(v.sale_price) : '—'}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}
